const API_BASE = "";

// Typed fetch wrapper — always sends credentials (session cookie)
async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body != null ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const err = new Error(`API ${res.status}: ${path}`);
    (err as ApiError).status = res.status;
    try {
      (err as ApiError).body = (await res.json()) as Record<string, unknown>;
    } catch {
      // non-JSON body
    }
    throw err;
  }

  // 204 No Content — return undefined cast to T
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

export interface ApiError extends Error {
  status: number;
  body?: Record<string, unknown>;
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof Error && "status" in err;
}

// Fetches a CSRF token then calls apiFetch with it in the header
async function apiFetchWithCsrf<T>(
  path: string,
  init: RequestInit,
): Promise<T> {
  const { csrfToken } = await apiFetch<{ csrfToken: string }>(
    "/api/v1/csrf-token",
  );
  return apiFetch<T>(path, {
    ...init,
    headers: { ...(init.headers ?? {}), "x-csrf-token": csrfToken },
  });
}

// ── Session ────────────────────────────────────────────────────────────────

export type SessionUser = {
  id: string;
  displayName: string | null;
  email: string | null;
  role: "admin" | "user";
  status: "active" | "pending";
  avatarUrl: string | null;
  primaryProvider: "github" | "discord" | "linkedin" | null;
};

export type SessionResponse = {
  user: SessionUser;
  expiresAt: string;
};

export function getSession(): Promise<SessionResponse> {
  return apiFetch<SessionResponse>("/api/v1/session");
}

// ── Auth actions ───────────────────────────────────────────────────────────

export async function signIn(
  provider: "github" | "discord" | "linkedin",
  callbackURL?: string,
): Promise<void> {
  const cb = callbackURL ?? window.location.pathname + window.location.search;
  const res = await apiFetch<{ url: string }>("/api/auth/sign-in/social", {
    method: "POST",
    body: JSON.stringify({ provider, callbackURL: cb }),
  });
  window.location.href = res.url;
}

export async function signOut(): Promise<void> {
  await apiFetch("/api/auth/sign-out", { method: "POST" });
}

export async function linkProvider(
  provider: "discord" | "linkedin",
): Promise<void> {
  // 1. Set _dg_linking cookie — auth.ts hook reads this on callback to write PROVIDER_LINKED audit event
  await apiFetchWithCsrf<{ ok: boolean }>(
    "/api/v1/account/providers/link-prepare",
    { method: "POST", body: JSON.stringify({ provider }) },
  );
  // 2. Use better-auth's dedicated /link-social endpoint (not sign-in/social)
  const res = await apiFetch<{ url: string }>("/api/auth/link-social", {
    method: "POST",
    body: JSON.stringify({
      provider,
      callbackURL: `/account?linked=${provider}`,
    }),
  });
  window.location.href = res.url;
}

export function unlinkProvider(
  provider: "github" | "discord" | "linkedin",
): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/account/providers/${provider}`, {
    method: "DELETE",
  });
}

// ── Apps (public listing) ──────────────────────────────────────────────────

export type AppSection = {
  id: string;
  name: string;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type App = {
  id: string;
  slug: string;
  subdomain: string | null;
  name: string;
  description: string | null;
  iconUrl: string | null;
  url: string | null;
  accessType: "hidden" | "public" | "private" | "restricted";
  passcodeType: "code" | "phrase" | null;
  hasMixedContent: boolean;
  showOnDashboard: boolean;
  subdomainEnabled: boolean;
  enabled: boolean;
  appType: "hosted" | "placeholder" | "file-display" | "redirect";
  versioningEnabled: boolean;
  maxVersions: number | null;
  maxFileSizeMb: number;
  sectionId: string | null;
  sortOrder: number;
  createdBy: string | null;
  port?: number;
  createdAt: string;
  updatedAt: string;
};

export function getApps(): Promise<{ data: App[]; sections: AppSection[] }> {
  return apiFetch<{ data: App[]; sections: AppSection[] }>("/api/v1/apps");
}

// ── Invite ─────────────────────────────────────────────────────────────────

export function validateInvite(token: string): Promise<{ valid: boolean }> {
  return apiFetch<{ valid: boolean }>(`/api/v1/invite/${token}`);
}

// ── Account — profile / preferences ───────────────────────────────────────

export function updateProfile(data: {
  displayName?: string;
  primaryProvider?: string;
}): Promise<{ ok: boolean }> {
  return apiFetchWithCsrf<{ ok: boolean }>("/api/v1/account/profile", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function updatePreferences(data: {
  theme?: "light" | "dark" | "lilac-light" | "lilac-dark";
  timezone?: string;
}): Promise<{ ok: boolean }> {
  return apiFetchWithCsrf<{ ok: boolean }>("/api/v1/account/preferences", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ── Account — providers ────────────────────────────────────────────────────

export type LinkedProvider = {
  id: string;
  providerId: string;
  providerEmail: string | null;
  createdAt: string;
};

export function getProviders(): Promise<{ data: LinkedProvider[] }> {
  return apiFetch<{ data: LinkedProvider[] }>("/api/v1/account/providers");
}

// ── Account — sessions ─────────────────────────────────────────────────────

export type UserSession = {
  id: string;
  createdAt: string;
  lastActiveAt: string;
  expiresAt: string;
  userAgent: string | null;
  isCurrent: boolean;
};

export function getSessions(): Promise<{ data: UserSession[] }> {
  return apiFetch<{ data: UserSession[] }>("/api/v1/account/sessions");
}

export function revokeSession(id: string): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/account/sessions/${id}`, {
    method: "DELETE",
  });
}

export function revokeAllOtherSessions(): Promise<void> {
  return apiFetchWithCsrf<void>("/api/v1/account/sessions", {
    method: "DELETE",
  });
}

// ── Account — audit log ────────────────────────────────────────────────────

export type AccountLogEntry = {
  id: string;
  eventType: string;
  provider: string | null;
  outcome: string | null;
  createdAt: string;
};

export function getAccountLogs(cursor?: string): Promise<{
  data: AccountLogEntry[];
  nextCursor: string | null;
}> {
  const qs = cursor ? `?cursor=${cursor}` : "";
  return apiFetch(`/api/v1/account/logs${qs}`);
}

// ── Account — delete ───────────────────────────────────────────────────────

export function deleteAccount(): Promise<void> {
  return apiFetchWithCsrf<void>("/api/v1/account", {
    method: "DELETE",
    body: JSON.stringify({ confirmation: "DELETE" }),
  });
}

// ── Admin — users ──────────────────────────────────────────────────────────

export type AdminUser = {
  id: string;
  displayName: string | null;
  email: string | null;
  avatarUrl: string | null;
  role: "admin" | "user";
  status: "pending" | "active" | "suspended" | "rejected" | "deleted";
  primaryProvider: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  hasActiveSession: boolean;
  providers: string[];
};

export type PaginatedResponse<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
};

export function getAdminUsers(params?: {
  page?: number;
  search?: string;
  role?: string;
  status?: string;
}): Promise<PaginatedResponse<AdminUser>> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.search) qs.set("search", params.search);
  if (params?.role) qs.set("role", params.role);
  if (params?.status) qs.set("status", params.status);
  const q = qs.toString();
  return apiFetch(`/api/v1/admin/users${q ? `?${q}` : ""}`);
}

export function updateAdminUser(
  id: string,
  data: { status?: string; role?: string },
): Promise<{ ok: boolean }> {
  return apiFetchWithCsrf<{ ok: boolean }>(`/api/v1/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteAdminUser(id: string): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/admin/users/${id}`, {
    method: "DELETE",
  });
}

// ── Admin — invites ────────────────────────────────────────────────────────

export type AdminInvite = {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedBy: string | null;
  invitedBy: string;
  status: "pending" | "accepted" | "expired";
};

export function getAdminInvites(
  page?: number,
): Promise<PaginatedResponse<AdminInvite>> {
  const qs = page && page > 1 ? `?page=${page}` : "";
  return apiFetch(`/api/v1/admin/invites${qs}`);
}

export function sendInvite(
  email: string,
): Promise<{ id: string; token: string }> {
  return apiFetchWithCsrf<{ id: string; token: string }>(
    "/api/v1/admin/invites",
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
  );
}

export function resendInvite(
  id: string,
): Promise<{ id: string; token: string }> {
  return apiFetchWithCsrf<{ id: string; token: string }>(
    `/api/v1/admin/invites/${id}/resend`,
    { method: "POST" },
  );
}

export function deleteInvite(id: string): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/admin/invites/${id}`, {
    method: "DELETE",
  });
}

// ── Admin — apps ───────────────────────────────────────────────────────────

export function getAdminApps(): Promise<{ data: App[] }> {
  return apiFetch<{ data: App[] }>("/api/v1/admin/apps");
}

export function createAdminApp(data: {
  slug?: string;
  name: string;
  description?: string;
  accessType?: string;
  appType?: "hosted" | "placeholder" | "file-display" | "redirect";
  url?: string;
  maxFileSizeMb?: number;
}): Promise<{ id: string; slug: string; port: number | null }> {
  return apiFetchWithCsrf<{ id: string; slug: string; port: number }>(
    "/api/v1/admin/apps",
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function updateAdminApp(
  id: string,
  data: Partial<{
    slug: string;
    subdomain: string | null;
    name: string;
    description: string | null;
    url: string | null;
    accessType: string;
    hasMixedContent: boolean;
    showOnDashboard: boolean;
    subdomainEnabled: boolean;
    enabled: boolean;
    appType: "hosted" | "placeholder" | "file-display" | "redirect";
    versioningEnabled: boolean;
    maxVersions: number | null;
    maxFileSizeMb: number;
    sectionId: string | null;
    sortOrder: number;
  }>,
): Promise<App> {
  return apiFetchWithCsrf<App>(`/api/v1/admin/apps/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteAdminApp(id: string): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/admin/apps/${id}`, {
    method: "DELETE",
  });
}

// ── Admin — app files ──────────────────────────────────────────────────────

export type AppFile = {
  id: string;
  appId: string;
  filename: string;
  fileType: string;
  fileExtension: string;
  fileSize: number;
  storagePath: string;
  version: number;
  isActive: boolean;
  uploadedBy: string;
  uploadedAt: string;
};

export async function uploadAppFile(
  slug: string,
  file: File,
): Promise<AppFile> {
  const { csrfToken } = await apiFetch<{ csrfToken: string }>(
    "/api/v1/csrf-token",
  );
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`/api/v1/admin/apps/${slug}/file`, {
    method: "POST",
    credentials: "include",
    headers: { "x-csrf-token": csrfToken },
    body: formData,
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status}: /api/v1/admin/apps/${slug}/file`);
    (err as ApiError).status = res.status;
    try {
      (err as ApiError).body = (await res.json()) as Record<string, unknown>;
    } catch {
      // non-JSON body
    }
    throw err;
  }
  return res.json() as Promise<AppFile>;
}

export function getAppFiles(slug: string): Promise<{ data: AppFile[] }> {
  return apiFetch<{ data: AppFile[] }>(`/api/v1/admin/apps/${slug}/files`);
}

export function deleteAppFile(slug: string): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/admin/apps/${slug}/file`, {
    method: "DELETE",
  });
}

export function restoreAppFileVersion(
  slug: string,
  fileId: string,
): Promise<AppFile> {
  return apiFetchWithCsrf<AppFile>(
    `/api/v1/admin/apps/${slug}/file/${fileId}/restore`,
    {
      method: "PUT",
    },
  );
}

export function deleteAppFileVersion(
  slug: string,
  fileId: string,
): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/admin/apps/${slug}/file/${fileId}`, {
    method: "DELETE",
  });
}

// ── Admin — sections ───────────────────────────────────────────────────────

export type AdminSection = AppSection & { appCount: number };

export function getAdminSections(): Promise<{ data: AdminSection[] }> {
  return apiFetch<{ data: AdminSection[] }>("/api/v1/admin/sections");
}

export function createAdminSection(name: string): Promise<AppSection> {
  return apiFetchWithCsrf<AppSection>("/api/v1/admin/sections", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export function updateAdminSection(
  id: string,
  name: string,
): Promise<AppSection> {
  return apiFetchWithCsrf<AppSection>(`/api/v1/admin/sections/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export function deleteAdminSection(id: string): Promise<void> {
  return apiFetchWithCsrf<void>(`/api/v1/admin/sections/${id}`, {
    method: "DELETE",
  });
}

export function reorderAdminSections(
  sections: { id: string; sortOrder: number }[],
): Promise<void> {
  return apiFetchWithCsrf<void>("/api/v1/admin/sections/reorder", {
    method: "PATCH",
    body: JSON.stringify({ sections }),
  });
}

export function reorderAdminApps(
  items: { id: string; sortOrder: number; sectionId: string | null }[],
): Promise<void> {
  return apiFetchWithCsrf<void>("/api/v1/admin/apps/reorder", {
    method: "PATCH",
    body: JSON.stringify({ apps: items }),
  });
}

// ── Admin — audit logs ─────────────────────────────────────────────────────

export type AuditLogEntry = {
  id: string;
  userId: string | null;
  eventType: string;
  provider: string | null;
  appId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  outcome: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export function getAdminAuditLogs(params?: {
  cursor?: string;
  userId?: string;
  eventType?: string;
}): Promise<{ data: AuditLogEntry[]; nextCursor: string | null }> {
  const qs = new URLSearchParams();
  if (params?.cursor) qs.set("cursor", params.cursor);
  if (params?.userId) qs.set("userId", params.userId);
  if (params?.eventType) qs.set("eventType", params.eventType);
  const q = qs.toString();
  return apiFetch(`/api/v1/admin/audit-logs${q ? `?${q}` : ""}`);
}
